import { queryOptions } from '@tanstack/react-query';

import { queryClient } from '@/services/query-client';

import { fetchCountries } from '../data-provider/countries';
import type { Countries, Country } from '../models';
import { CACHE_KEYS } from '../types';
import { logger } from '../utils/logger';

export const countriesQueryOptions = ({ enabled = true }: { enabled?: boolean } = {}) =>
  queryOptions({
    enabled,
    queryFn: async ({ signal }) => {
      const countries = await fetchCountries({ signal });
      put(countries.list);
      return countries;
    },
    queryKey: CACHE_KEYS.countries(),
    staleTime: 1000 * 60 * 60,
  });

const INTERNAL_QUERY_COUNTRIES_KEY = countriesQueryOptions().queryKey;

const getCountryFallback = (countryId: string): Country => {
  logger.warn(`Country "${countryId}" was not found. Verify the data model.`);

  return {
    id: countryId,
    label: countryId,
  };
};

export const put = (countries: Country[]) => {
  const data = queryClient.getQueryData<Countries>(INTERNAL_QUERY_COUNTRIES_KEY);

  const newCountries = countries.reduce(
    (acc, country) => {
      acc[String(country.id)] = country;
      return acc;
    },
    { ...(data?.map ?? {}) },
  );

  queryClient.setQueryData(INTERNAL_QUERY_COUNTRIES_KEY, {
    list: Object.values(newCountries),
    map: newCountries,
  });
};

export const getAll = (): Countries | undefined => {
  return queryClient.getQueryData(INTERNAL_QUERY_COUNTRIES_KEY);
};

export const find = async (countryId: string): Promise<Country> => {
  const data = queryClient.getQueryData<Countries>(INTERNAL_QUERY_COUNTRIES_KEY);
  const country = data?.map[countryId];

  if (country) {
    return country;
  }

  try {
    const newData = await queryClient.query(countriesQueryOptions());
    if (newData.map[countryId]) {
      put(newData.list);
      return newData.map[countryId];
    }
  } catch {
    return getCountryFallback(countryId);
  }

  return getCountryFallback(countryId);
};

export const findAll = async (countryIds: string[]): Promise<Country[]> => {
  const data = queryClient.getQueryData<Countries>(INTERNAL_QUERY_COUNTRIES_KEY);

  const missingIds: string[] = [];
  const countries: Country[] = [];

  for (const countryId of countryIds) {
    const country = data?.map[countryId];
    if (country) {
      countries.push(country);
    } else {
      missingIds.push(countryId);
    }
  }

  if (!missingIds.length) {
    return countries;
  }

  try {
    const newData = await queryClient.query(countriesQueryOptions());
    put(newData.list);
    for (const missingId of missingIds) {
      const country = newData.map[missingId];
      if (country) {
        countries.push(country);
      } else {
        countries.push(getCountryFallback(missingId));
      }
    }
  } catch {
    for (const missingId of missingIds) {
      countries.push(getCountryFallback(missingId));
    }
  }

  return countries;
};
